/**
 * The quota meter and the limit dialog.
 *
 * Neither can be server-rendered. The landing page is edge-cached for 24 h, so
 * a rendered meter would show one visitor a stranger's remaining minutes for a
 * day. Both therefore come from an uncached endpoint after load — which is
 * exactly why the pre-load state matters: a meter that reads "90 of 90" before
 * `/api/quota` resolves is a lie that happens to be right sometimes, and the
 * one time it is wrong is the one time the user cared.
 *
 * Five states, and the difference between the last two is the point:
 *   loading  — we have not asked yet
 *   normal   — asked and answered
 *   low      — under 10 minutes, amber
 *   empty    — zero left
 *   unknown  — asked and did not get an answer. NOT zero.
 */

import { ApiError, getQuota } from './api.ts';
import type { QuotaResponse } from './api-types.ts';
import { attachDialog, type DialogHandle } from './dialog.ts';
import { qs, qsa, setData } from './dom.ts';
import { track } from './events.ts';
import { minutesRemaining, resetsIn } from './format.ts';

/** Amber below this many minutes. UI_UX_BRIEF § Components. */
const LOW_MINUTES = 10;

export type MeterState = 'loading' | 'normal' | 'low' | 'empty' | 'unknown';

let latest: QuotaResponse | null = null;

export function latestQuota(): QuotaResponse | null {
  return latest;
}

function variant(root: HTMLElement, name: string, show: boolean): void {
  const node = qs<HTMLElement>(`[data-meter-variant="${name}"]`, root);
  if (node) node.hidden = !show;
}

function paint(meter: HTMLElement, state: MeterState, quota: QuotaResponse | null): void {
  setData(meter, 'state', state);
  variant(meter, 'loading', state === 'loading');
  variant(meter, 'unknown', state === 'unknown');
  variant(meter, 'value', state === 'normal' || state === 'low' || state === 'empty');

  if (!quota) {
    meter.style.setProperty('--meter-pct', '0%');
    return;
  }
  const remaining = qs<HTMLElement>('[data-slot="remaining"]', meter);
  const budget = qs<HTMLElement>('[data-slot="budget"]', meter);
  if (remaining) remaining.textContent = String(minutesRemaining(quota.asr_seconds_remaining));
  if (budget) budget.textContent = String(Math.round(quota.asr_seconds_budget / 60));
  const pct =
    quota.asr_seconds_budget > 0
      ? Math.max(0, Math.min(100, (quota.asr_seconds_remaining / quota.asr_seconds_budget) * 100))
      : 0;
  meter.style.setProperty('--meter-pct', `${pct.toFixed(1)}%`);
}

export function stateFor(quota: QuotaResponse): MeterState {
  const minutes = minutesRemaining(quota.asr_seconds_remaining);
  if (quota.asr_seconds_remaining <= 0) return 'empty';
  if (minutes < LOW_MINUTES) return 'low';
  return 'normal';
}

/** Repaint every meter on the page from a quota payload. */
export function applyQuota(quota: QuotaResponse): void {
  latest = quota;
  for (const meter of qsa<HTMLElement>('.meter')) paint(meter, stateFor(quota), quota);
}

/**
 * Fetch and render. Never rejects: a meter that cannot be filled shows the
 * `unknown` state, and the page keeps working — the quota check that matters
 * is the server's, at submission time.
 */
export async function loadQuota(): Promise<QuotaResponse | null> {
  const meters = qsa<HTMLElement>('.meter');
  if (meters.length === 0) return null;
  try {
    const quota = await getQuota();
    applyQuota(quota);
    return quota;
  } catch (err) {
    for (const meter of meters) paint(meter, 'unknown', null);
    if (!(err instanceof ApiError)) console.error('[quota]', err);
    return null;
  }
}

/* ── the limit dialog (E10) ───────────────────────────────────────────── */

let limitDialog: DialogHandle | null = null;

function ensureDialog(): DialogHandle | null {
  if (limitDialog) return limitDialog;
  const el = qs<HTMLDialogElement>('dialog[data-state="E10"]');
  if (!el) return null;
  limitDialog = attachDialog(el, {
    onClose: (reason) => track('limit_dialog_closed', { reason }),
  });
  for (const cta of qsa<HTMLElement>('[data-cta="convert"]', el)) {
    cta.addEventListener('click', () => track('limit_dialog_cta', { target: 'convert' }));
  }
  return limitDialog;
}

/**
 * Show E10.
 *
 * `budget_seconds` and `resets_in_s` come from whichever payload triggered it —
 * the problem's `quota` extension on a refused submission, or the last
 * `/api/quota`. The countdown is the rolling window (when the oldest debit
 * ages out), never a fixed midnight, and it is omitted rather than shown as
 * zero when the window holds no debits.
 */
export function showLimitDialog(opts: {
  budgetSeconds?: number | undefined;
  resetsInS?: number | null | undefined;
}): boolean {
  const handle = ensureDialog();
  if (!handle) {
    console.error('[quota] the limit dialog is not on this page');
    return false;
  }
  const root = handle.el;
  const budget = opts.budgetSeconds ?? latest?.asr_seconds_budget;
  if (typeof budget === 'number') {
    const node = qs<HTMLElement>('[data-slot="budget_minutes"]', root);
    if (node) node.textContent = String(Math.round(budget / 60));
  }
  const reset = resetsIn(opts.resetsInS ?? latest?.resets_in_s ?? null);
  const h = qs<HTMLElement>('[data-slot="resets_h"]', root);
  const m = qs<HTMLElement>('[data-slot="resets_m"]', root);
  if (reset) {
    if (h) h.textContent = reset.h;
    if (m) m.textContent = reset.m;
  }
  handle.open();
  track('limit_dialog_shown', { state: 'E10' });
  return true;
}

/**
 * The dialog must never block reading an already-open transcript, so nothing
 * here opens it on load. It opens from one place only: a submission the server
 * refused for quota.
 */
export function closeLimitDialog(): void {
  limitDialog?.close('api');
}
